
import { Component, namespace, Vue } from "nuxt-property-decorator";

import {
  validateWiderrufStep1,
  WiderrufSupport,
} from "~/utils/widerrufFormValidation";
import { SubmitWiderrufPayload } from "~/store/widerruf";
import { provideDateService } from "~/operations/date";

const WiderrufModule = namespace("widerruf");

interface WiderrufUserInput {
  firstName: string;
  lastName: string;
  email: string;
  orderNumber: string;
  notice: string;
  website: string;
}

@Component({})
export default class WiderrufForm extends Vue {
  @WiderrufModule.Action("submitWiderruf")
  _submitWiderruf!: (payload: SubmitWiderrufPayload) => Promise<boolean>;

  @WiderrufModule.State("receivedAt")
  receivedAt!: string | null;

  @WiderrufModule.State("errorMessage")
  errorMessage!: string;

  @WiderrufModule.Mutation("setErrorMessage")
  _setErrorMessage!: (message: string) => void;

  step = 1;
  submitting = false;

  created(): void {
    this._setErrorMessage("");
  }

  get receivedAtDate(): string {
    return this.receivedAt
      ? provideDateService(this.receivedAt)
          .tz("Europe/Berlin")
          .format("DD.MM.YYYY")
      : "";
  }

  get receivedAtTime(): string {
    return this.receivedAt
      ? provideDateService(this.receivedAt).tz("Europe/Berlin").format("HH:mm")
      : "";
  }

  userInput: WiderrufUserInput = {
    firstName: "",
    lastName: "",
    email: "",
    orderNumber: "",
    notice: "",
    website: "",
  };

  support: WiderrufSupport = {
    firstName: "",
    lastName: "",
    email: "",
    orderNumber: "",
  };

  readonly NOTICE_WORD_LIMIT = 250;

  splitWords(text: string): string[] {
    return text.trim().split(/\s+/).filter(Boolean);
  }

  get noticeWordCount(): number {
    return this.splitWords(this.userInput.notice).length;
  }

  onNoticeInput(value: string): void {
    const words = this.splitWords(value);
    this.userInput.notice =
      words.length > this.NOTICE_WORD_LIMIT
        ? words.slice(0, this.NOTICE_WORD_LIMIT).join(" ")
        : value;
  }

  get noticeSummaryValue(): string {
    return this.userInput.notice.trim() ? this.userInput.notice : "—";
  }

  get summaryItems(): Array<{ label: string; value: string }> {
    return [
      { label: "Vorname", value: this.userInput.firstName },
      { label: "Nachname", value: this.userInput.lastName },
      { label: "E-Mail-Adresse", value: this.userInput.email },
      { label: "Bestellnummer", value: this.userInput.orderNumber },
      { label: "Anmerkungen", value: this.noticeSummaryValue },
    ];
  }

  goToConfirm(): void {
    const isValid = validateWiderrufStep1(
      {
        firstName: this.userInput.firstName,
        lastName: this.userInput.lastName,
        email: this.userInput.email,
        orderNumber: this.userInput.orderNumber,
      },
      this.support,
    );

    if (isValid) {
      this.step = 2;
    }
  }

  backToCapture(): void {
    this.step = 1;
  }

  async confirm(): Promise<void> {
    this.submitting = true;
    const ok = await this._submitWiderruf({ ...this.userInput });
    this.submitting = false;

    if (ok) {
      this.step = 3;
    }
  }

  goHome(): void {
    this.$router.push("/pelletspreise");
  }
}
